import { Timestamp } from 'firebase/firestore';
import {
  chain, get, has, isEmpty, map, omit, set, size,
} from 'lodash';
import ProgramAutomationActionsDB from 'src/fb/programAutomationActions-db';
import ProgramBehaviourDataGroupsDB from 'src/fb/programBehaviourDataGroups-db';
import ProgramBehaviourDataItemsDB from 'src/fb/programBehaviourDataItems-db';
import ProgramFilesDB from 'src/fb/programFiles-db';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import { logEvent } from 'src/utils/LogEvent';

import GenericDB from './generic-db';

export default class ProgramsDB extends GenericDB {
  constructor() {
    super('programs');
  }

  async readWithTargets(ids = null, clientId = null) {
    let programs;

    if (ids !== null) {
      programs = await this.readMultiple(ids);
    } else {
      programs = await this.readAll([['client', '==', clientId]]);
    }

    const programActions = programs.map(async (program) => {
      const programTargetsDb = new ProgramTargetsDB(program.id);
      const retval = {
        ...program,
      };
      retval.targets = await programTargetsDb.readAll([], 'order');

      return retval;
    });

    return Promise.all(programActions);
  } // readWithTargets()

  async readWithTargetsAndScoring(ids = null, clientId = null) {
    let programs;

    if (ids !== null) {
      programs = await this.readMultiple(ids);
    } else {
      programs = await this.readAll([['client', '==', clientId]]);
    }

    const programActions = programs.map(async (program) => {
      const programTargetsDb = new ProgramTargetsDB(program.id);
      const retval = {
        ...program,
      };
      retval.targets = await programTargetsDb.readAll([], 'order');

      return retval;
    });
    return Promise.all(programActions);
  } // readWithTargetsAndScoring()

  /**
   * Duplicates targets and returns a map of old and new target IDs
   *
   * @param {Array} programTargets - The targets to duplicate.
   * @param {string} newProgramId - The new program id.
   * @param {string|null} originalParentTargetId - The current parent target id.
   * @param {string|null} newParentTargetId -The new parent target id.
   * @param {Object} targetProps - Default target properties.
   * @returns {Map} The map of old and new target IDs.
   */
  async copyProgramTargets(
    programTargets,
    newProgramId,
    originalParentTargetId = null,
    newParentTargetId = null,
    targetProps = {},
  ) {
    if (!newProgramId) {
      throw new Error('Duplicating program error. Program ID is missing');
    }

    const programTargetsDb = new ProgramTargetsDB();
    const targetIdMap = new Map();

    const targetActions = chain(programTargets)
      .filter({ parentTarget: originalParentTargetId })
      .map(async (target) => {
        const newTargetObj = {
          ...target,
          id: null,
          ...targetProps,
          program: newProgramId,
          trialsToAcquire: null,
          sessionsToAcquire: null,
          parentTarget: newParentTargetId,
          ...(target.previousPromptLevel && { previousPromptLevel: null }),
        };

        programTargetsDb.setProgramId(newProgramId);
        const newTarget = await programTargetsDb.create(newTargetObj);
        targetIdMap.set(target.id, newTarget.id);

        const targetHasSubTargets = chain(programTargets).filter({ parentTarget: target.id }).size().value() > 0;

        if (targetHasSubTargets) {
          const subTargetIdsMap = await this.copyProgramTargets(
            programTargets,
            newProgramId,
            target.id,
            newTarget.id,
            targetProps,
          );
          subTargetIdsMap.forEach((oldSubTargetId, newSubTargetId) => {
            targetIdMap.set(oldSubTargetId, newSubTargetId);
          });
        }
      })
      .value();
    await Promise.all(targetActions);

    return targetIdMap;
  } // copyProgramTargets()

  /**
   * Copy behavior groups and items from one program to another
   * @param {*} fromProgramId
   * @param {*} toProgramId
   */
  // eslint-disable-next-line class-methods-use-this
  async copyBehaviourGroups(fromProgramId, toProgramId) {
    const programBehaviourDataGroupsDb = new ProgramBehaviourDataGroupsDB(fromProgramId);
    const groups = await programBehaviourDataGroupsDb.readAll([['isDeleted', '==', false]]);
    programBehaviourDataGroupsDb.setProgramId(toProgramId);
    const groupActions = groups.map(async (fromGroup) => programBehaviourDataGroupsDb
      .create(omit(fromGroup, 'id'))
      .then(async (toGroup) => {
        const programBehaviourDataItemsDb = new ProgramBehaviourDataItemsDB(fromProgramId, fromGroup.id);
        const items = await programBehaviourDataItemsDb.readAll([['isDeleted', '==', false]]);
        programBehaviourDataItemsDb.setProgramId(toProgramId);
        programBehaviourDataItemsDb.setProgramBehaviourDataGroupId(toGroup.id);
        const itemActions = items.map((fromItem) => programBehaviourDataItemsDb.create(omit(fromItem, 'id')));
        return Promise.all(itemActions);
      }));
    await Promise.all(groupActions);
  } // copyBehaviourGroups()

  /**
   * Copy automation action items from one program to another
   * @param {*} fromProgramId
   * @param {*} toProgramId
   */
  // eslint-disable-next-line class-methods-use-this
  async copyAutomationActions(fromProgramId, toProgramId) {
    const programAutomationActionsDb = new ProgramAutomationActionsDB(fromProgramId);
    const programAutomationActions = await programAutomationActionsDb.readAll();

    programAutomationActionsDb.setProgramId(toProgramId);
    const automationActionActions = programAutomationActions.map(
      async (fromAutomationAction) => programAutomationActionsDb.create(omit(fromAutomationAction, 'id')),
    );
    await Promise.all(automationActionActions);
  } // copyAutomationActions()

  /**
   * Duplicate a program and change specified props for program, targets and scoring
   * @param {*} programId
   * @param {*} programProps
   * @param {*} targetProps
   * @param {*} duplicateFiles
   */
  async duplicateProgram(
    programId,
    programProps = {},
    targetProps = {},
    duplicateFiles = true,
  ) {
    const [program] = await this.readWithTargetsAndScoring([programId]);
    const isClientLibraryContextChange = has(programProps, 'client')
      && programProps.client !== program.client;

    // Clear targets progress
    // Set the count of the target with status 'waiting'
    const { targets = [], targetsPerStatus = [] } = program;
    const programTargetsCount = size(targets);
    const newTargetsPerStatus = map(targetsPerStatus, (target) => ({
      ...target,
      count: target.status === 'waiting' ? programTargetsCount : 0,
    }));

    // Reset program info not relevant for library
    const programObj = {
      ...omit(program, ['targets', 'scoring']),
      id: null,
      ...programProps,
      isDeleted: false,
      filesRemoved: false,
      isLocked: false,
      targetsPerStatus: newTargetsPerStatus,
      statusChanges: {
        [program.status]: Timestamp.now(),
      },
      lastRunInSession: null,
    };

    // Add program to store
    const newProgram = await this.create(programObj);

    // Copy targets
    // Drop trialsToAcquire and sessionsToAcquire
    const programTargets = get(program, 'targets', null);
    let targetIdsMap = {};
    if (programTargets) {
      try {
        targetIdsMap = await this.copyProgramTargets(programTargets, newProgram.id, null, null, targetProps);

        if (program.srTarget && targetIdsMap.has(program.srTarget)) {
          newProgram.srTarget = targetIdsMap.get((program.srTarget));
          this.set({ srTarget: newProgram.srTarget }, newProgram.id);
        }
      } catch (err) {
        logEvent(err, {
          context: {
            context_data: { programId },
          },
        });
        throw new Error(err);
      }
    }
    // Copy behavior targets
    await Promise.all([
      this.copyBehaviourGroups(programId, newProgram.id),
      this.copyAutomationActions(programId, newProgram.id),
    ]);

    // Copy program files
    if (duplicateFiles) {
      const programFilesDb = new ProgramFilesDB(program.id);
      const programFiles = await programFilesDb.readAll();
      if (programFiles) {
        const programFileActions = programFiles.map((programFile) => {
          const programFileObj = {
            ...programFile,
            id: null,
          };

          programFilesDb.setProgramId(newProgram.id);
          return programFilesDb.create(programFileObj);
        });
        await Promise.all(programFileActions);
      }
    }

    // Update references to duplicated program and targets in progress chart settings
    const dataSeriesSettings = get(programObj, ['progressSettings', 'dataSeriesSettings'], {});
    const progressSettings = {};

    const phaseLines = get(dataSeriesSettings, 'phaseLines');
    if (!isEmpty(phaseLines)) {
      set(
        progressSettings,
        ['dataSeriesSettings', 'phaseLines'],
        map(phaseLines, (phaseLine) => ({
          ...phaseLine,
          ...(phaseLine.program && (phaseLine.program === program.id || isClientLibraryContextChange)
            ? { program: newProgram.id }
            : {}),
          ...(targetIdsMap.has(phaseLine.target) ? { target: targetIdsMap.get(phaseLine.target) } : {}),
        })),
      );
    }

    const filterProgramIds = get(dataSeriesSettings, ['postloadFilter', 'programs', 'programId']);
    if (!isEmpty(filterProgramIds)) {
      const remappedFilterProgramIds = filterProgramIds
        .map((filterProgramId) => {
          if (isClientLibraryContextChange && filterProgramId !== program.id) {
            return null;
          }
          return filterProgramId === program.id ? newProgram.id : filterProgramId;
        })
        .filter((filterProgramId) => filterProgramId != null);

      set(
        progressSettings,
        ['dataSeriesSettings', 'postloadFilter', 'programs', 'programId'],
        isEmpty(remappedFilterProgramIds) && isClientLibraryContextChange
          ? [newProgram.id]
          : remappedFilterProgramIds,
      );
    }

    const filterTargetIds = get(dataSeriesSettings, ['postloadFilter', 'programs', 'targetId']);
    if (!isEmpty(filterTargetIds)) {
      set(
        progressSettings,
        ['dataSeriesSettings', 'postloadFilter', 'programs', 'targetId'],
        filterTargetIds
          .map((filterTargetId) => {
            if (isClientLibraryContextChange && !targetIdsMap.has(filterTargetId)) {
              return null;
            }
            return targetIdsMap.has(filterTargetId)
              ? targetIdsMap.get(filterTargetId)
              : filterTargetId;
          })
          .filter((filterTargetId) => filterTargetId != null),
      );
    }
    if (!isEmpty(progressSettings)) {
      this.set({ progressSettings }, newProgram.id);
    }

    return newProgram;
  } // duplicateProgram()

  /**
   * Delete a program and all associated targets, scoring and behavior related data
   */
  async deleteProgram(programId) {
    await this.deleteSubcollection(programId, 'programTargets');

    const programBehaviourDataGroupsDb = new ProgramBehaviourDataGroupsDB(programId);
    const groups = await programBehaviourDataGroupsDb.readAll();
    const deleteGroupsActions = groups.map(async (group) => {
      return programBehaviourDataGroupsDb.deleteSubcollection(group.id, 'programBehaviourDataItems');
    });
    await Promise.all(deleteGroupsActions);
    await this.deleteSubcollection(programId, 'programBehaviourDataGroups');
    await this.deleteSubcollection(programId, 'programAutomationActions');
    await this.deleteSubcollection(programId, 'programFormItems');

    await this.delete(programId);
  } // deleteProgram()

  /**
   * Load programs that use a specific element
   *
   * @param {string} key - field name in the program document
   * @param {string} elementId
   * @param {string} clinic
   * @returns {Promise<array> List of programs}
   */
  readByElement(key, elementId, clinic, maxElements = null) {
    return this.readAll([
      [key, '==', elementId],
      ['clinic', '==', clinic],
      ['isDeleted', '==', false],
    ], null, null, maxElements);
  } // readByElement()
}
